import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowDown, CheckCircle2, XCircle, Layers, Cog, Beaker, Droplets, Zap, Wind, Atom, ShieldCheck, Thermometer, AlertTriangle } from 'lucide-react';
import type { ProcessSimulation, DischargeLimits, ProcessStage } from '../types/calculator';

interface ProcessFlowProps {
  simulation: ProcessSimulation | undefined;
  limits: DischargeLimits;
}

const safeNum = (n: number | undefined | null) => (typeof n === 'number' ? n : 0);

export default function ProcessFlow({ simulation, limits }: ProcessFlowProps) {
  const [activeStage, setActiveStage] = useState<number | null>(null);

  if (!simulation || simulation.stages.length === 0) return null;

  const stageMeta = (name: string) => {
    const map: Record<string, { color: string; bg: string; border: string; badge: string; icon: React.ReactNode }> = {
      'Pre-Treatment': { color: 'text-slate-400', bg: 'bg-slate-800/30', border: 'border-slate-700', badge: 'bg-slate-600', icon: <Cog className="w-5 h-5" /> },
      'Primary': { color: 'text-rb-brand', bg: 'bg-rb-brand/5', border: 'border-rb-brand/20', badge: 'bg-rb-brand', icon: <Beaker className="w-5 h-5" /> },
      'Secondary': { color: 'text-emerald-400', bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', badge: 'bg-emerald-500', icon: <Droplets className="w-5 h-5" /> },
      'Tertiary': { color: 'text-cyan-400', bg: 'bg-cyan-500/5', border: 'border-cyan-500/20', badge: 'bg-cyan-500', icon: <Zap className="w-5 h-5" /> },
      'Polishing': { color: 'text-teal-400', bg: 'bg-teal-500/5', border: 'border-teal-500/20', badge: 'bg-teal-500', icon: <ShieldCheck className="w-5 h-5" /> },
      'Sludge': { color: 'text-red-400', bg: 'bg-red-500/5', border: 'border-red-500/20', badge: 'bg-red-500', icon: <Layers className="w-5 h-5" /> },
      'Disinfection': { color: 'text-indigo-400', bg: 'bg-indigo-500/5', border: 'border-indigo-500/20', badge: 'bg-indigo-500', icon: <Wind className="w-5 h-5" /> },
    };
    return map[name] || map['Pre-Treatment'];
  };

  const getPrevStage = (i: number) => i === 0 ? null : simulation.stages[i - 1];

  const formatValue = (val: number | undefined, unit?: string) => {
    const v = safeNum(val);
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k${unit ? ' ' + unit : ''}`;
    return `${v.toFixed(0)}${unit ? ' ' + unit : ''}`;
  };

  const compliance = (stage: ProcessStage) => {
    const checks = [
      { label: 'SS', value: stage.tss, limit: limits.tss, pass: safeNum(stage.tss) <= safeNum(limits.tss) },
      { label: 'BOD', value: stage.bod5, limit: limits.bod5, pass: safeNum(stage.bod5) <= safeNum(limits.bod5) },
      { label: 'COD', value: stage.cod, limit: limits.cod, pass: safeNum(stage.cod) <= safeNum(limits.cod) },
      { label: 'O&G', value: stage.oilGrease, limit: limits.oilGrease, pass: safeNum(stage.oilGrease) <= safeNum(limits.oilGrease) },
      { label: 'TDS', value: stage.tds, limit: limits.tds, pass: safeNum(stage.tds) <= safeNum(limits.tds) },
      { label: 'TKN', value: stage.tkn ?? (safeNum(stage.totalNitrogen) * 0.85), limit: limits.tkn, pass: safeNum(stage.tkn ?? (safeNum(stage.totalNitrogen) * 0.85)) <= safeNum(limits.tkn) },
      { label: 'TN', value: stage.totalNitrogen, limit: limits.totalNitrogen, pass: safeNum(stage.totalNitrogen) <= safeNum(limits.totalNitrogen) },
      { label: 'TP', value: stage.totalPhosphorus, limit: limits.totalPhosphorus, pass: safeNum(stage.totalPhosphorus) <= safeNum(limits.totalPhosphorus) },
      { label: 'pH', value: stage.ph, limitMin: limits.phMin, limitMax: limits.phMax, pass: safeNum(stage.ph) >= safeNum(limits.phMin) && safeNum(stage.ph) <= safeNum(limits.phMax) },
      { label: 'Temp', value: stage.temperature, limit: limits.temperature, pass: safeNum(stage.temperature) <= safeNum(limits.temperature) },
      { label: 'Cond.', value: stage.conductivity, limit: limits.conductivity, pass: safeNum(stage.conductivity) <= safeNum(limits.conductivity) },
    ];
    const passed = checks.filter(c => c.pass).length;
    return { checks, passed, total: checks.length };
  };

  const lastStage = simulation.stages[simulation.stages.length - 1];
  const finalCompliance = compliance(lastStage);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
          <Layers className="w-5 h-5 text-rb-brand" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white">Process Flow Diagram</h2>
          <p className="text-xs text-slate-500">Complete treatment train with mass balance, tank sizing, and compliance tracking</p>
        </div>
      </div>

      {/* Final Effluent Compliance Banner */}
      <div className={`p-5 rounded-xl border ${finalCompliance.passed === finalCompliance.total ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${finalCompliance.passed === finalCompliance.total ? 'bg-emerald-500' : 'bg-rb-brand'}`}>
              {finalCompliance.passed === finalCompliance.total ? <CheckCircle2 className="w-5 h-5 text-white" /> : <AlertTriangle className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">
                Final Effluent Compliance
                <span className={finalCompliance.passed === finalCompliance.total ? 'text-emerald-400 ml-2' : 'text-amber-400 ml-2'}>
                  {finalCompliance.passed === finalCompliance.total ? 'ALL LIMITS MET' : `${finalCompliance.total - finalCompliance.passed} FAIL`}
                </span>
              </h3>
              <p className="text-xs text-slate-500">Compared against discharge limits at the final treatment stage</p>
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {finalCompliance.checks.map((c, i) => (
              <div key={i} className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${c.pass ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                <span className="opacity-60 mr-1">{c.label}</span>
                {safeNum(c.value as number).toFixed(c.label === 'pH' ? 2 : 0)}
                <span className="ml-1">{c.pass ? '✓' : '✗'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Overall KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { label: 'BOD Removed', value: `${(safeNum(simulation.totalBodReduction) * 100).toFixed(1)}%`, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
          { label: 'COD Removed', value: `${(safeNum(simulation.totalCodReduction) * 100).toFixed(1)}%`, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
          { label: 'TSS Removed', value: `${(safeNum(simulation.totalTssReduction) * 100).toFixed(1)}%`, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
          { label: 'Final pH', value: safeNum(simulation.finalPh).toFixed(2), color: 'text-rb-brand', bg: 'bg-rb-brand/10', border: 'border-rb-brand/20' },
          { label: 'Total Volume', value: `${safeNum(simulation.totalTankVolumeM3).toLocaleString()} m³`, color: 'text-slate-300', bg: 'bg-slate-800/30', border: 'border-slate-700' },
          { label: 'Units', value: `${safeNum(simulation.equipmentCount)}`, color: 'text-slate-300', bg: 'bg-slate-800/30', border: 'border-slate-700' },
        ].map((kpi, i) => (
          <div key={i} className={`p-3 rounded-xl border ${kpi.border} ${kpi.bg} text-center`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{kpi.label}</div>
            <div className={`text-lg font-bold ${kpi.color} mt-1`}>{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Stage Cards */}
      <div className="space-y-0">
        {simulation.stages.map((stage, i) => {
          const prev = getPrevStage(i);
          const meta = stageMeta(stage.stageName);
          const isActive = activeStage === i;
          const comp = compliance(stage);
          const bodChange = prev ? prev.bod5 - stage.bod5 : 0;
          const tssChange = prev ? prev.tss - stage.tss : 0;
          const codChange = prev ? prev.cod - stage.cod : 0;

          return (
            <div key={i} className="relative">
              {/* Arrow connector */}
              {i > 0 && (
                <div className="flex flex-col items-center py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-px w-16 bg-slate-700" />
                    <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                      <ArrowDown className="w-3 h-3 text-slate-500" />
                    </div>
                    <div className="h-px w-16 bg-slate-700" />
                  </div>
                  <div className="text-[10px] text-slate-600 mt-0.5 flex gap-2">
                    {bodChange > 0 && <span className="text-emerald-400 font-bold">-{formatValue(bodChange)} BOD</span>}
                    {tssChange > 0 && <span className="text-cyan-400 font-bold">-{formatValue(tssChange)} TSS</span>}
                    {codChange > 0 && <span className="text-cyan-400 font-bold">-{formatValue(codChange)} COD</span>}
                  </div>
                </div>
              )}

              {/* Stage Card */}
              <div
                className={`rounded-xl border-2 transition-all duration-300 overflow-hidden cursor-pointer ${
                  isActive
                    ? comp.passed === comp.total ? 'border-emerald-400 shadow-lg shadow-emerald-500/10' : 'border-red-400 shadow-lg shadow-red-500/10'
                    : comp.passed === comp.total
                    ? 'border-slate-700 hover:border-slate-600'
                    : 'border-red-500/30 hover:border-red-500/50'
                } ${meta.bg}`}
                onClick={() => setActiveStage(isActive ? null : i)}
              >
                {/* Header */}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-12 h-12 rounded-xl ${meta.badge} flex flex-col items-center justify-center shadow-lg`}>
                      <span className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">Step</span>
                      <span className="text-xl font-bold text-white leading-none mt-0.5">{stage.stageNumber}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`text-sm font-bold ${meta.color}`}>{stage.stageName}</h3>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400 font-medium">
                          {stage.equipment.length} unit{stage.equipment.length !== 1 ? 's' : ''}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border flex items-center gap-1 ${
                          comp.passed === comp.total
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                        }`}>
                          {comp.passed === comp.total ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                          {comp.passed === comp.total ? 'COMPLIANT' : `${comp.total - comp.passed} FAIL`}
                        </span>
                        {stage.sludgeGeneration && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rb-brand/10 text-rb-brand font-bold border border-rb-brand/20">
                            Sludge: {stage.sludgeGeneration.volumeM3PerDay} m³/day
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{stage.equipment.map(e => e.name).join(' + ')}</p>
                    </div>

                    <div className="shrink-0 mt-1 w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isActive ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Key Results Row */}
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-2">
                    {[
                      { label: 'BOD', inVal: prev?.bod5 ?? stage.bod5, outVal: stage.bod5, unit: 'mg/L', limit: limits.bod5, color: 'text-emerald-400' },
                      { label: 'COD', inVal: prev?.cod ?? stage.cod, outVal: stage.cod, unit: 'mg/L', limit: limits.cod, color: 'text-cyan-400' },
                      { label: 'TSS', inVal: prev?.tss ?? stage.tss, outVal: stage.tss, unit: 'mg/L', limit: limits.tss, color: 'text-cyan-400' },
                      { label: 'pH', inVal: prev?.ph ?? stage.ph, outVal: stage.ph, unit: '', limitMin: limits.phMin, limitMax: limits.phMax, color: 'text-rb-brand' },
                      { label: 'O&G', inVal: prev?.oilGrease ?? stage.oilGrease, outVal: stage.oilGrease, unit: 'mg/L', limit: limits.oilGrease, color: 'text-rb-brand' },
                      { label: 'TKN', inVal: (prev?.tkn ?? safeNum(prev?.totalNitrogen) * 0.85), outVal: (stage.tkn ?? safeNum(stage.totalNitrogen) * 0.85), unit: 'mg/L', limit: limits.tkn, color: 'text-cyan-400' },
                    ].map((param, pi) => {
                      const inVal = safeNum(param.inVal as number);
                      const outVal = safeNum(param.outVal as number);
                      const change = inVal - outVal;
                      const pct = inVal > 0 ? (change / inVal) * 100 : 0;
                      const isReduction = change > 0;
                      let pass = true;
                      if (param.limit !== undefined) pass = outVal <= safeNum(param.limit);
                      if (param.limitMin !== undefined && param.limitMax !== undefined) {
                        pass = outVal >= safeNum(param.limitMin) && outVal <= safeNum(param.limitMax);
                      }
                      return (
                        <div key={pi} className={`p-2.5 rounded-lg bg-slate-800/50 border ${pass ? 'border-slate-800/50' : 'border-red-500/20'} h-full flex flex-col`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{param.label}</span>
                            {isReduction && pct > 1 && (
                              <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">-{pct.toFixed(0)}%</span>
                            )}
                          </div>
                          <div className="flex items-baseline gap-1.5">
                            {i > 0 && (
                              <span className="text-[10px] text-slate-600 line-through font-medium">
                                {inVal.toFixed(param.label === 'pH' ? 2 : 0)}{param.unit ? ` ${param.unit}` : ''}
                              </span>
                            )}
                            <span className={`text-sm font-bold ${pass ? param.color : 'text-red-400'}`}>
                              {outVal.toFixed(param.label === 'pH' ? 2 : 0)}{param.unit ? ` ${param.unit}` : ''}
                            </span>
                          </div>
                          {!pass && (
                            <div className="mt-1 text-[10px] text-red-400 font-bold bg-red-500/10 px-1.5 py-0.5 rounded inline-block">
                              {param.limit !== undefined && `Exceeds limit: ${param.limit} ${param.unit}`}
                              {param.limitMin !== undefined && `Outside range: ${param.limitMin}-${param.limitMax}`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-2 border-t border-slate-800/50">
                        {/* Tanks */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                          {stage.tanks.map((tank, ti) => (
                            <div key={ti} className="p-2.5 rounded-lg bg-slate-800/50 border border-slate-800/50 text-xs">
                              <div className="font-semibold text-white">{tank.name}</div>
                              <div className="text-slate-500">{tank.volumeM3} m³ | {tank.hrt} hr HRT</div>
                              <div className="text-slate-600">{tank.dimensions}</div>
                            </div>
                          ))}
                        </div>
                        {/* Chemicals */}
                        {stage.chemicals.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {stage.chemicals.map((chem, ci) => (
                              <span key={ci} className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] text-slate-400 font-medium">
                                {chem.name}: {chem.dose} {chem.unit}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Sludge */}
                        {stage.sludgeGeneration && (
                          <div className="p-2.5 rounded-lg bg-rb-brand/5 border border-rb-brand/15 text-xs text-slate-400">
                            <span className="font-bold text-rb-brand">DAF Sludge:</span>{' '}
                            {safeNum(stage.sludgeGeneration?.volumeM3PerDay)} m³/day, {safeNum(stage.sludgeGeneration?.drySolidsKgPerDay).toLocaleString()} kg/day dry solids
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
